// @ts-strict-ignore
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { ProjectModel } from '@project/model/project.model';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ModelLoadingService } from '@article/service/model-loading.service';
import { ArticleModelDataHolder } from '@3d-scene/model3d/cache/model-data';
import { CompressionSimpleTextValueVO, MetaElementUsageVO, MetaElementVO, SimpleTextValueVO, TextureAreaUsageVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { AlertHelper } from '@shared/alert/alert.helper';
import { RouteNavigationService } from '@services/route-navigation.service';
import { OverlayService } from '@shared/overlay/overlay.service';
import { OverlayBackground } from '@shared/overlay/overlay.type';
import { OpenMarkingSystemLabelEditorService } from '@project/components/label/label-editor/service/open-marking-system-label-editor.service';
import { Injectable } from '@angular/core';
import { Log } from '@shared/debug/log';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { TextSizeCheckRequestVO, TextSizeCheckVO } from '@3d-scene/manager/text-size-check.vo';
import { ArticleConnectionPropertyDef } from '@article/constants/article-connection-property-def';
import { ArticleUtils } from '@article/util/article-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { MetaElementBehaviorPropertyDef } from '@project/constants/meta-element-behavior-property-def';
import { TextSizeCheckManager } from '@3d-scene/manager/text-size-check.manager';
import { ArticleManager } from '@article/manager/article.manager';
import { UpdateLabelingCompressionProcessor } from '@3d-scene/processor/update-labeling-compression.processor';
import { AttachArticleUsageService } from '@project/service/attach-article-usage.service';
import { MarkingSystemProjectCreationServiceDelegator } from './marking-system-project-creation-service.delegator';
import { ArticleUsageService } from '@article/service/article-usage.service';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { IsProjectType } from '@project/model/is-project-type';
import { HtmlUtils } from '@shared/util/html-utils';

@Injectable({ providedIn: 'root' })
export class MarkingSystemProjectCreationService {
  private article: ArticleVO;
  private color: string;
  private projectCreationRequired: boolean;
  private connectionTextSizeCheckClasses: string[] = [];

  constructor(
    markingSystemProjectCreationServiceDelegator: MarkingSystemProjectCreationServiceDelegator,
    private projectModel: ProjectModel,
    private projectModelService: ProjectModelService,
    private projectArticleService: ProjectArticleService,
    private modelLoadingService: ModelLoadingService,
    private alertHelper: AlertHelper,
    private routeNavigationService: RouteNavigationService,
    private openMarkingSystemLabelEditorService: OpenMarkingSystemLabelEditorService,
    private overlayService: OverlayService,
    private textSizeCheckManager: TextSizeCheckManager,
    private articleManager: ArticleManager,
    private updateLabelingCompressionProcessor: UpdateLabelingCompressionProcessor,
    private attachArticleUsageService: AttachArticleUsageService,
    private articleUsageService: ArticleUsageService
  ) {
    markingSystemProjectCreationServiceDelegator.markingSystemProjectCreationService = this;
  }

  public async createNewProject(article: ArticleVO, color?: string): Promise<void> {
    this.overlayService.requestOverlayShow(OverlayBackground.color);

    this.projectCreationRequired = true;
    this.article = article;
    this.color = color;
    this.projectModelService.currentProjectType = "MARKINGSYSTEM";
    const usage: ArticleUsageVO = this.projectArticleService.createNewArticleUsage(article.identity, article.version);
    await this.attachArticleUsageService.attachArticleUsageToProject(usage, null, true);
  }

  public async projectRefreshedSuccess(): Promise<void> {
    if (!this.projectCreationRequired) {
      return;
    }

    let articleModelDataHolder: ArticleModelDataHolder;
    try {
      articleModelDataHolder = await this.modelLoadingService.loadModel(this.article, true);
    } catch (error) {
      Log.error('Could not load model: ', error);
      this.errorHandler();
      return;
    }

    this.article = articleModelDataHolder.article;
    const articleUsage: ArticleUsageVO = ProjectUtils.getFirstArticleUsage(this.projectModel.currentProject)
    if (articleModelDataHolder.article.metaElements != null) {
      for (const metaElement of this.article.metaElements) {
        if (metaElement instanceof TextureAreaVO) {
          this.checkAndUpdateTextureArea(articleUsage, metaElement.name);
        }
      }
    }
    await this.updateMarkingSystemPredefinedAreas();
  }

  public async updateMarkingSystemPredefinedAreas(): Promise<void> {
    if (IsProjectType.markingSystem(this.projectModel.currentProject)) {
      this.connectionTextSizeCheckClasses = [];
      const articleUsage = ProjectUtils.tryGetFirstArticleUsage(this.projectModel.currentProject);

      if (!articleUsage || !this.articleManager.isArticleCached(articleUsage.articleId, articleUsage.articleVersion)) return;

      const article: ArticleVO = this.articleUsageService.getArticleByArticleUsage(articleUsage);

      const textSizeCheckRequests: TextSizeCheckRequestVO[] = (article?.metaElements ?? [])
        .map(meta => this.findTextSizeCheckRequest(meta))
        .filter(req => Boolean(req));

      if (textSizeCheckRequests.length > 0) {
        const success = await this.textSizeCheckManager.retrieveTextSizeCheckInfos(textSizeCheckRequests);
        if (!success) {
          await this.alertHelper.showError('global.title.error', 'global.message.unexpected_error');
        }
      }
      await this.continueUpdateMarkingSystemPredefinedAreas();
    }
  }

  private findTextSizeCheckRequest(metaElementVO: MetaElementVO | TextureAreaVO): TextSizeCheckRequestVO | undefined {
    if (!(metaElementVO instanceof TextureAreaVO)) return undefined;

    const textSizeCheckClass: string = PropertyUtils.getStringPropertyValue(
      metaElementVO.behaviorProperties,
      MetaElementBehaviorPropertyDef.TEXT_CHECK_CLASS
    );
    if (!(textSizeCheckClass != null && textSizeCheckClass.length > 0)) return;

    const req: TextSizeCheckRequestVO = new TextSizeCheckRequestVO();
    req.textSizeCheckClass = textSizeCheckClass;
    const markerCardNr: number = PropertyUtils.getIntPropertyValue(
      metaElementVO.behaviorProperties,
      MetaElementBehaviorPropertyDef.MARKER_CARD_NR
    );
    if (!isNaN(markerCardNr)) {
      const key: string = markerCardNr.toString();
      this.connectionTextSizeCheckClasses[key] = textSizeCheckClass;
    }
    if (this.textSizeCheckManager.isRetrievalNecessary(req)) {
      return req;
    }
  }

  private async continueUpdateMarkingSystemPredefinedAreas(): Promise<void> {
    let articleUsage: ArticleUsageVO = ProjectUtils.getFirstArticleUsage(this.projectModel.currentProject);
    const article: ArticleVO = this.articleUsageService.getArticleByArticleUsage(articleUsage);
    let articleNumberString: string = this.projectModel.currentProject.articleNumberLong?.reducedArticleNumberLong;
    if (articleNumberString == null && article != null) {
      articleNumberString = article.articleNo;
    }
    let articleNumberString2: string = '';
    const spacing: number = PropertyUtils.getFloatPropertyValue(article.connectionRequirements, ArticleConnectionPropertyDef.SPACING);
    const ts: TextSizeCheckVO[] = this.textSizeCheckManager.getTextSizeCheck(this.connectionTextSizeCheckClasses['102'], spacing);
    if (ts != null && ts.length > 0) {
      const textSizeCheck: TextSizeCheckVO = ts[0] as TextSizeCheckVO;
      if (textSizeCheck.chars < articleNumberString.length) {
        const firstZ: number = articleNumberString.indexOf('Z');
        if (firstZ > -1) {
          articleNumberString2 = articleNumberString.substring(firstZ);
          articleNumberString = articleNumberString.slice(0, firstZ);
        }
      }
    }
    for (const metaElementUsage of articleUsage.metaElementUsages) {
      const metaElementVO: MetaElementVO = ArticleUtils.findMetaElementByName(article, metaElementUsage.identity.metaElementName);
      const markerCardNr: number = PropertyUtils.getIntPropertyValue(metaElementVO.behaviorProperties, MetaElementBehaviorPropertyDef.MARKER_CARD_NR);
      if (markerCardNr === 101) {
        this.applyMarkingSystemPredefinedText(metaElementUsage, markerCardNr, spacing, 'WAGO');
      } else if (markerCardNr === 102) {
        this.applyMarkingSystemPredefinedText(metaElementUsage, markerCardNr, spacing, articleNumberString);
      } else if (markerCardNr === 103) {
        this.applyMarkingSystemPredefinedText(metaElementUsage, markerCardNr, spacing, articleNumberString2);
      }
    }
    await this.updateLabelingCompressionProcessor.updateTextures();
    await this.markingSystemPredefinedAreasUpdated();
  }

  private applyMarkingSystemPredefinedText(meta: MetaElementUsageVO, markerCardNr: number, spacing: number, text: string): void {
    const textureAreaUsageVO: TextureAreaUsageVO = meta as TextureAreaUsageVO;
    if (textureAreaUsageVO == null) {
      return;
    }
    const textValue: SimpleTextValueVO = textureAreaUsageVO.textValue as SimpleTextValueVO;
    if (textValue == null) {
      return;
    }
    const ts: TextSizeCheckVO[] = this.textSizeCheckManager.getTextSizeCheck(this.connectionTextSizeCheckClasses[markerCardNr.toString()], spacing);
    let textSizeCheck: TextSizeCheckVO = null;
    if (ts != null && ts.length > 0) {
      textSizeCheck = ts[0] as TextSizeCheckVO;
    }
    const fontName: string = textSizeCheck != null ? textSizeCheck.fontName : 'smartFont';
    const fontSize: string = (textSizeCheck != null && !isNaN(textSizeCheck.fontSize)) ? textSizeCheck.fontSize.toString() : '10';
    textValue.text = HtmlUtils.createLegacyFontParagraph(text, {
      align: 'CENTER',
      fontFace: fontName,
      fontSize: fontSize,
      color: '#000000',
      bold: textSizeCheck == null || textSizeCheck.fontBold >= 1,
    });
    if (textSizeCheck != null) {
      textValue.rotation = textSizeCheck.rotation === 1 ? 270 : 0;
    }
  }

  public errorHandler(): void {
    if (!this.projectCreationRequired) {
      return;
    }
    this.projectCreationRequired = false;
    this.alertHelper.showError('global.title.error', 'markingSystem.errorMessage.savingModelFailed');
    this.overlayService.requestOverlayHide(OverlayBackground.color);
  }

  private checkAndUpdateTextureArea(articleUsage: ArticleUsageVO, metaElementName: string): void {
    const textureAreaUsage = ArticleUsageUtils.findTextureAreaUsageByName(articleUsage, metaElementName);
    if (textureAreaUsage) {
      textureAreaUsage.textValue = new CompressionSimpleTextValueVO();
    } else {
      ArticleUsageUtils.newTextureAreaUsage(articleUsage, metaElementName);
    }
  }

  private async markingSystemPredefinedAreasUpdated(): Promise<void> {
    if (!this.projectCreationRequired) {
      return;
    }
    await this.routeNavigationService.toConfigurator();
    await this.openMarkingSystemLabelEditorService.openMarkingSystemLabelEditorHandler({
        isNewProject: true,
        articleTechnicalInfoBundles: null,
        article: this.article,
        colorString: this.color
      });
    this.overlayService.requestOverlayHide(OverlayBackground.color);
    this.projectCreationRequired = false;
  }
}
